import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Loader2,
  Star,
  Gift,
  ChefHat,
  User,
  Mail,
  Phone,
  Award,
  TrendingUp,
  CheckCircle,
  QrCode,
  Timer,
  X,
  Cake,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

// Progress bar to next level
function LevelProgress({ points, currentLevel, nextLevel }) {
  const from = currentLevel?.min_points || 0;
  const to = nextLevel?.min_points || (from + 500);
  const progress = Math.min(100, Math.round(((points - from) / (to - from)) * 100));

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-xs">
        <span style={{ color: currentLevel?.color || "#F97316" }} className="font-semibold">
          {currentLevel?.name || "Básico"}
        </span>
        {nextLevel && (
          <span className="text-white/60">
            {to - points} pts para {nextLevel.name}
          </span>
        )}
      </div>
      <div className="h-2 bg-white/20 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${progress}%`, background: nextLevel?.color || currentLevel?.color || "#F97316" }}
        />
      </div>
    </div>
  );
}

// Digital Loyalty Card
function LoyaltyCard({ customer, config, restaurantName, restaurantLogo, currentLevel, nextLevel, qrCode }) {
  const primaryColor = config?.primary_color || "#F97316";
  const secondaryColor = config?.secondary_color || "#1F2937";

  return (
    <div
      className="relative rounded-3xl overflow-hidden shadow-2xl"
      style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)` }}
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 -translate-y-1/3 translate-x-1/3"
        style={{ background: "white" }} />
      <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-10 translate-y-1/3 -translate-x-1/3"
        style={{ background: "white" }} />

      <div className="relative p-6 text-white">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            {restaurantLogo ? (
              <img src={restaurantLogo} alt="Logo" className="w-10 h-10 rounded-xl object-cover bg-white/20" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <ChefHat className="w-5 h-5" />
              </div>
            )}
            <div>
              <p className="text-xs opacity-70 uppercase tracking-widest">
                {config?.program_name || "Club de Fidelización"}
              </p>
              <p className="font-bold text-base leading-tight">{restaurantName}</p>
            </div>
          </div>
          {currentLevel && (
            <div
              className="px-3 py-1 rounded-full text-xs font-bold"
              style={{ background: currentLevel.color || "#FFD700", color: "#1F2937" }}
            >
              {currentLevel.name}
            </div>
          )}
        </div>

        {/* Customer name */}
        <p className="text-2xl font-black tracking-wide mb-1">{customer.name}</p>
        {customer.email && <p className="text-xs opacity-60 mb-5">{customer.email}</p>}

        {/* Points */}
        <div className="flex items-end gap-6 mb-5">
          <div>
            <p className="text-5xl font-black leading-none">{(customer.points || 0).toLocaleString()}</p>
            <p className="text-xs opacity-70 mt-1">puntos disponibles</p>
          </div>
          {qrCode && (
            <div className="ml-auto">
              <div className="bg-white rounded-xl p-2">
                <img src={qrCode} alt="Mi QR" className="w-16 h-16" />
              </div>
              <p className="text-xs opacity-60 text-center mt-1">Mi QR</p>
            </div>
          )}
        </div>

        {/* Level progress */}
        {(currentLevel || nextLevel) && (
          <LevelProgress points={customer.points || 0} currentLevel={currentLevel} nextLevel={nextLevel} />
        )}

        {/* Stats footer */}
        <div className="flex gap-6 mt-4 pt-4 border-t border-white/20 text-xs opacity-70">
          <div>
            <p className="font-semibold text-white text-sm">{customer.total_visits || 0}</p>
            <p>visitas</p>
          </div>
          <div>
            <p className="font-semibold text-white text-sm">{(customer.total_spent || 0).toFixed(2)}€</p>
            <p>gastado</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Reward QR Dialog
function RewardQRDialog({ open, onClose, restaurantId, reward, customerToken, primaryColor }) {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!open || !reward) return;
    setQrData(null);
    setSecondsLeft(0);
    generateQR();
  }, [open, reward]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!qrData || secondsLeft <= 0) return;
    const interval = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) { clearInterval(interval); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [qrData, secondsLeft]);

  const generateQR = async () => {
    setLoading(true);
    try {
      const data = await api.generateRewardQR(restaurantId, reward.reward_id, customerToken);
      setQrData(data);
      setSecondsLeft(data.expires_in_seconds || 900);
    } catch (err) {
      toast.error(err.message || "Error al generar QR");
    } finally {
      setLoading(false);
    }
  };

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const isExpired = qrData && secondsLeft === 0;
  const progressPct = qrData ? Math.round((secondsLeft / (qrData.expires_in_seconds || 900)) * 100) : 100;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5" style={{ color: primaryColor }} />
            Canjear recompensa
          </DialogTitle>
          <DialogDescription>
            Muestra este QR al personal del restaurante para validar tu canje.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Reward info */}
          <div className="flex items-center gap-3 p-3 rounded-xl border" style={{ borderColor: primaryColor + "40", background: primaryColor + "08" }}>
            <Gift className="w-5 h-5 shrink-0" style={{ color: primaryColor }} />
            <div>
              <p className="font-bold text-sm">{reward?.name}</p>
              <p className="text-xs text-muted-foreground">{reward?.points_required} puntos</p>
            </div>
          </div>

          {/* QR or loader */}
          <div className="flex flex-col items-center justify-center min-h-[200px]">
            {loading ? (
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
                <p className="text-sm text-muted-foreground">Generando QR...</p>
              </div>
            ) : isExpired ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <X className="w-8 h-8 text-red-500" />
                </div>
                <p className="text-sm font-medium text-red-500">QR expirado</p>
                <Button size="sm" onClick={generateQR} style={{ background: primaryColor, color: "white" }}>
                  Generar nuevo QR
                </Button>
              </div>
            ) : qrData ? (
              <div className="space-y-3 w-full flex flex-col items-center">
                {/* QR image */}
                <div className="p-4 bg-white rounded-2xl shadow-inner border-2" style={{ borderColor: primaryColor + "40" }}>
                  <img src={qrData.qr_code} alt="QR de canje" className="w-44 h-44" data-testid="reward-qr-image" />
                </div>

                {/* Timer */}
                <div className="w-full space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Timer className="w-3 h-3" /> Válido durante
                    </span>
                    <span className={`font-mono font-bold ${secondsLeft < 60 ? "text-red-500" : ""}`}
                      style={secondsLeft >= 60 ? { color: primaryColor } : {}}>
                      {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-1000"
                      style={{
                        width: `${progressPct}%`,
                        background: secondsLeft < 60 ? "#ef4444" : primaryColor
                      }}
                    />
                  </div>
                </div>

                <p className="text-xs text-center text-muted-foreground">
                  Una vez validado, se descontarán <strong>{reward?.points_required} puntos</strong> de tu cuenta.
                </p>
              </div>
            ) : null}
          </div>

          <Button variant="outline" className="w-full" onClick={onClose}>Cerrar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// "Guardar en Google Wallet" — fetches a signed Save-to-Wallet link for the
// customer. Renders nothing when Wallet is not configured / plan-locked, so the
// customer never sees a broken button.
function SaveToGoogleWalletButton({ restaurantId, customerId, token }) {
  const [saveUrl, setSaveUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.getGoogleWalletLink(restaurantId, customerId, token);
        if (alive && data?.configured && data?.save_url) setSaveUrl(data.save_url);
      } catch {
        /* locked / not configured / network — silently hide the button */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [restaurantId, customerId, token]);

  if (loading || !saveUrl) return null;

  return (
    <a
      href={saveUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="save-to-google-wallet-btn"
      className="flex items-center justify-center gap-2.5 w-full py-3.5 rounded-2xl bg-[#202124] text-white font-semibold text-sm shadow-md hover:bg-black transition-colors"
    >
      <Wallet className="w-5 h-5" />
      Guardar en Google Wallet
    </a>
  );
}

export default function CustomerPortalPage() {  const { restaurantId } = useParams();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState("loading"); // loading | register | card
  const [portalInfo, setPortalInfo] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [authMode, setAuthMode] = useState(null); // "google" | "form"
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", birthday: "" });
  const [consentGdpr, setConsentGdpr] = useState(false);
  const [processing, setProcessing] = useState(false);
  const hasProcessedRef = useRef(false);

  // Reward QR dialog
  const [rewardQROpen, setRewardQROpen] = useState(false);
  const [selectedRewardForQR, setSelectedRewardForQR] = useState(null);
  const [customerToken, setCustomerToken] = useState(null);

  useEffect(() => {
    loadPortalInfo();
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Google Auth callback
  useEffect(() => {
    if (hasProcessedRef.current) return;
    const hash = window.location.hash;
    if (!hash.includes("session_id=")) return;
    hasProcessedRef.current = true;

    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get("session_id");
    if (sessionId) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      handleGoogleCallback(sessionId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check saved customer
  useEffect(() => {
    const savedToken = sessionStorage.getItem(`loyalty_token_${restaurantId}`);
    const savedCustomerId = sessionStorage.getItem(`loyalty_customer_${restaurantId}`);
    if (savedToken && savedCustomerId && step === "register") {
      loadCustomerCard(savedCustomerId, savedToken);
    }
  }, [step, restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPortalInfo = async () => {
    try {
      const info = await api.getPublicLoyaltyInfo(restaurantId);
      setPortalInfo(info);

      // Check if already logged in
      const savedToken = sessionStorage.getItem(`loyalty_token_${restaurantId}`);
      const savedCustomerId = sessionStorage.getItem(`loyalty_customer_${restaurantId}`);
      if (savedToken && savedCustomerId) {
        setCustomerToken(savedToken);
        await loadCustomerCard(savedCustomerId, savedToken);
      } else {
        setStep("register");
      }
    } catch (error) {
      console.error(error);
      setStep("error");
    }
  };

  const loadCustomerCard = async (customerId, token) => {
    try {
      const data = await api.getCustomerPortalCard(restaurantId, customerId, token);
      setCustomer(data.customer);
      setCardData(data);
      setStep("card");
    } catch (error) {
      // Token expired or invalid, show register
      sessionStorage.removeItem(`loyalty_token_${restaurantId}`);
      sessionStorage.removeItem(`loyalty_customer_${restaurantId}`);
      setStep("register");
    }
  };

  const handleGoogleCallback = async (sessionId) => {
    setProcessing(true);
    try {
      // Recover the GDPR consent from sessionStorage (set before OAuth redirect).
      // If absent (user hit the endpoint directly), reject cleanly.
      const storedConsent = sessionStorage.getItem(`loyalty_consent_${restaurantId}`) === "1";
      if (!storedConsent) {
        toast.error("Para completar el registro con Google debes aceptar la política de privacidad");
        setStep("register");
        setProcessing(false);
        return;
      }
      const result = await api.googleRegisterCustomer(restaurantId, sessionId, true);
      sessionStorage.removeItem(`loyalty_consent_${restaurantId}`);
      const { customer: c, token } = result;
      sessionStorage.setItem(`loyalty_token_${restaurantId}`, token);
      sessionStorage.setItem(`loyalty_customer_${restaurantId}`, c.customer_id);
      setCustomerToken(token);
      await loadCustomerCard(c.customer_id, token);
      toast.success(result.is_new ? `¡Bienvenido, ${c.name}!` : `¡Bienvenido de nuevo, ${c.name}!`);
    } catch (error) {
      toast.error(error.message || "Error al registrarse con Google");
      setStep("register");
    } finally {
      setProcessing(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!consentGdpr) {
      toast.error("Debes aceptar la política de privacidad para continuar");
      return;
    }
    // Persist consent across the Google OAuth roundtrip (it leaves the SPA)
    sessionStorage.setItem(`loyalty_consent_${restaurantId}`, "1");
    const redirectUrl = window.location.origin + `/loyalty/${restaurantId}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return toast.error("El nombre es requerido");
    if (!consentGdpr) return toast.error("Debes aceptar la política de privacidad");
    setProcessing(true);
    try {
      const result = await api.registerCustomerPortal(restaurantId, { ...formData, consent_gdpr: true });
      const { customer: c, token } = result;
      sessionStorage.setItem(`loyalty_token_${restaurantId}`, token);
      sessionStorage.setItem(`loyalty_customer_${restaurantId}`, c.customer_id);
      setCustomerToken(token);
      await loadCustomerCard(c.customer_id, token);
      toast.success(result.is_new ? `¡Bienvenido, ${c.name}!` : `¡Bienvenido de nuevo, ${c.name}!`);
    } catch (error) {
      toast.error(error.message || "Error al registrarse");
    } finally {
      setProcessing(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(`loyalty_token_${restaurantId}`);
    sessionStorage.removeItem(`loyalty_customer_${restaurantId}`);
    setCustomer(null);
    setCardData(null);
    setStep("register");
    setAuthMode(null);
  };

  const primaryColor = portalInfo?.config?.primary_color || "#F97316";
  const restaurantName = portalInfo?.restaurant?.name || "Restaurante";

  // LOADING
  if (step === "loading" || processing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4"
        style={{ background: `linear-gradient(135deg, ${primaryColor}20, #f9fafb)` }}>
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg"
          style={{ background: primaryColor }}>
          <ChefHat className="w-7 h-7 text-white" />
        </div>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: primaryColor }} />
        <p className="text-muted-foreground">Un momento...</p>
      </div>
    );
  }

  // ERROR
  if (step === "error") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6">
        <ChefHat className="w-14 h-14 text-muted-foreground" />
        <h2 className="text-xl font-bold">Portal no encontrado</h2>
        <p className="text-muted-foreground text-center">El programa de fidelización no existe o ha sido desactivado.</p>
      </div>
    );
  }

  // REGISTER
  if (step === "register") {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: `linear-gradient(160deg, ${primaryColor}15 0%, white 50%)` }}>
        {/* Header */}
        <div className="text-center pt-12 pb-8 px-4">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg"
            style={{ background: primaryColor }}>
            {portalInfo?.restaurant?.logo_url ? (
              <img src={portalInfo.restaurant.logo_url} alt="Logo" className="w-full h-full rounded-2xl object-cover" />
            ) : (
              <ChefHat className="w-8 h-8 text-white" />
            )}
          </div>
          <h1 className="text-2xl font-black" style={{ color: primaryColor }}>{restaurantName}</h1>
          <p className="text-gray-500 mt-1">{portalInfo?.config?.program_name || "Club de Fidelización"}</p>

          {/* Benefits */}
          <div className="flex justify-center gap-6 mt-6">
            {[
              { icon: Star, text: `${portalInfo?.config?.points_per_euro || 1} pts/€` },
              { icon: Gift, text: "Recompensas" },
              { icon: Award, text: "Niveles VIP" }
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex flex-col items-center gap-1">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: primaryColor + "20" }}>
                  <Icon className="w-5 h-5" style={{ color: primaryColor }} />
                </div>
                <p className="text-xs text-gray-500 font-medium">{text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center px-4 max-w-md mx-auto w-full">
          {!authMode ? (
            /* Choose auth method */
            <div className="w-full space-y-3">
              <p className="text-center text-sm text-gray-500 mb-6">Únete gratis para acumular puntos y conseguir recompensas</p>

              {/* GDPR consent */}
              <label className="flex gap-2.5 items-start p-3 rounded-xl bg-gray-50 border border-gray-200 cursor-pointer" data-testid="portal-consent-block">
                <input
                  type="checkbox"
                  className="mt-1 w-4 h-4 accent-current"
                  checked={consentGdpr}
                  onChange={(e) => setConsentGdpr(e.target.checked)}
                  data-testid="portal-consent-checkbox"
                  style={{ accentColor: primaryColor }}
                />
                <span className="text-xs text-gray-600 leading-relaxed">
                  He leído y acepto la{" "}
                  <a
                    href={`/legal/privacidad/${restaurantId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium"
                    style={{ color: primaryColor }}
                    data-testid="portal-privacy-link"
                  >
                    política de privacidad
                  </a>{" "}
                  de {restaurantName} y consiento el tratamiento de mis datos para fines de fidelización, conforme al RGPD.
                </span>
              </label>

              <Button
                className="w-full h-14 text-base font-bold rounded-2xl shadow-md"
                style={{ background: primaryColor, color: "white" }}
                onClick={handleGoogleLogin}
                disabled={!consentGdpr}
                data-testid="google-register-btn"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="white" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="white" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="white" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="white" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continuar con Google
              </Button>

              <div className="relative flex items-center py-2">
                <div className="flex-1 border-t border-gray-200" />
                <span className="mx-4 text-xs text-gray-400">o con formulario</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>

              <Button
                variant="outline"
                className="w-full h-14 text-base rounded-2xl"
                onClick={() => setAuthMode("form")}
                disabled={!consentGdpr}
                data-testid="form-register-btn"
              >
                <User className="w-5 h-5 mr-3" />
                Registrarse con formulario
              </Button>

              {/* Existing member note */}
              <p className="text-xs text-center text-gray-400 mt-4">
                ¿Ya eres miembro? Vuelve a registrarte con el mismo email para acceder a tu tarjeta.
              </p>
            </div>
          ) : (
            /* Form registration */
            <div className="w-full">
              <button
                className="text-sm text-gray-400 mb-6 flex items-center gap-1"
                onClick={() => setAuthMode(null)}
              >
                ← Volver
              </button>
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Nombre completo *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      className="pl-10 h-12"
                      placeholder="Tu nombre"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      data-testid="portal-name-input"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      className="pl-10 h-12"
                      type="email"
                      placeholder="tu@email.com"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      data-testid="portal-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Teléfono</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      className="pl-10 h-12"
                      placeholder="+34 600 000 000"
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      data-testid="portal-phone-input"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Fecha de cumpleaños</Label>
                  <div className="relative">
                    <Cake className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      className="pl-10 h-12"
                      type="date"
                      value={formData.birthday ? `2000-${formData.birthday}` : ""}
                      onChange={e => {
                        const val = e.target.value;
                        if (val) {
                          const parts = val.split("-");
                          setFormData({ ...formData, birthday: `${parts[1]}-${parts[2]}` });
                        } else {
                          setFormData({ ...formData, birthday: "" });
                        }
                      }}
                      data-testid="portal-birthday-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 text-base font-bold rounded-2xl mt-2"
                  style={{ background: primaryColor }}
                  disabled={processing}
                  data-testid="portal-submit-btn"
                >
                  {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : "Unirme al programa"}
                </Button>
              </form>
            </div>
          )}
        </div>

        {/* Available rewards preview */}
        {portalInfo?.rewards?.length > 0 && (
          <div className="px-4 pb-10 max-w-md mx-auto w-full mt-8">
            <p className="text-sm font-semibold text-gray-500 mb-3">Recompensas disponibles</p>
            <div className="space-y-2">
              {portalInfo.rewards.slice(0, 3).map(r => (
                <div key={r.reward_id} className="flex items-center justify-between p-3 bg-white rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: primaryColor + "20" }}>
                      <Gift className="w-4 h-4" style={{ color: primaryColor }} />
                    </div>
                    <p className="text-sm font-medium">{r.name}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">{r.points_required} pts</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // CARD VIEW
  if (step === "card" && cardData && customer) {
    const { config, rewards, current_level: currentLevel, next_level: nextLevel, qr_code: qrCode } = cardData;
    const logo = portalInfo?.restaurant?.logo_url || cardData?.restaurant?.logo_url;

    return (
      <div className="min-h-screen flex flex-col" style={{ background: `linear-gradient(160deg, ${primaryColor}15 0%, white 60%)` }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 max-w-md mx-auto w-full">
          <div className="flex items-center gap-2">
            {logo ? (
              <img src={logo} alt="Logo" className="w-8 h-8 rounded-lg object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: primaryColor }}>
                <ChefHat className="w-4 h-4 text-white" />
              </div>
            )}
            <span className="font-bold text-sm">{restaurantName}</span>
          </div>
          <button onClick={handleLogout} className="text-xs text-gray-400 underline">Salir</button>
        </div>

        <div className="flex-1 px-4 max-w-md mx-auto w-full space-y-5 pb-8">
          {/* Loyalty Card */}
          <LoyaltyCard
            customer={customer}
            config={config}
            restaurantName={restaurantName}
            restaurantLogo={logo}
            currentLevel={currentLevel}
            nextLevel={nextLevel}
            qrCode={qrCode}
          />

          {/* Save loyalty card to Google Wallet (offline pass) */}
          <SaveToGoogleWalletButton
            restaurantId={restaurantId}
            customerId={customer.customer_id}
            token={customerToken}
          />

          {/* Welcome message */}
          {currentLevel && (
            <div
              className="flex items-center gap-3 p-4 rounded-2xl text-white"
              style={{ background: currentLevel.color || primaryColor }}
            >
              <Award className="w-6 h-6 shrink-0" />
              <div>
                <p className="font-bold text-sm">Nivel {currentLevel.name}</p>
                <p className="text-xs opacity-80">{currentLevel.benefits || "¡Gracias por tu fidelidad!"}</p>
              </div>
            </div>
          )}

          {/* Rewards */}
          {rewards?.length > 0 && (
            <div>
              <p className="font-bold text-sm mb-3 flex items-center gap-2">
                <Gift className="w-4 h-4" style={{ color: primaryColor }} />
                Recompensas disponibles
              </p>
              <div className="space-y-2">
                {rewards.map(r => {
                  const canRedeem = (customer.points || 0) >= r.points_required;
                  const progress = Math.min(100, Math.round(((customer.points || 0) / r.points_required) * 100));
                  return (
                    <div
                      key={r.reward_id}
                      className={`p-4 rounded-2xl border bg-white shadow-sm ${canRedeem ? "border-green-200" : "border-gray-100 opacity-80"}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-semibold text-sm">{r.name}</p>
                        {canRedeem ? (
                          <Badge className="bg-green-100 text-green-700 border-0 text-xs">¡Disponible!</Badge>
                        ) : (
                          <span className="text-xs text-gray-400">{r.points_required - (customer.points || 0)} pts faltan</span>
                        )}
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${progress}%`, background: canRedeem ? "#22c55e" : primaryColor }}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-400">{r.points_required} puntos necesarios</p>
                        {canRedeem && (
                          <button
                            className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full text-white"
                            style={{ background: primaryColor }}
                            onClick={() => { setSelectedRewardForQR(r); setRewardQROpen(true); }}
                            data-testid={`redeem-qr-${r.reward_id}`}
                          >
                            <QrCode className="w-3 h-3" /> Generar QR
                          </button>
                        )}
                      </div>
                      {canRedeem && (
                        <p className="text-xs mt-1" style={{ color: primaryColor + "aa" }}>
                          Toca "Generar QR" y muéstraselo al personal del restaurante
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reward QR Dialog */}
          <RewardQRDialog
            open={rewardQROpen}
            onClose={() => { setRewardQROpen(false); setSelectedRewardForQR(null); }}
            restaurantId={restaurantId}
            reward={selectedRewardForQR}
            customerToken={customerToken}
            primaryColor={primaryColor}
          />

          {/* How to earn points */}
          <div className="bg-white rounded-2xl border shadow-sm p-4">
            <p className="font-bold text-sm mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" style={{ color: primaryColor }} />
              Cómo ganar puntos
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                <p>Gana <strong>{config?.points_per_euro || 1} punto(s)</strong> por cada euro consumido</p>
              </div>
              {config?.points_per_visit > 0 && (
                <div className="flex items-center gap-3 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                  <p>+<strong>{config.points_per_visit}</strong> puntos adicionales por visita</p>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm">
                <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                <p>El personal registrará tu consumo en cada visita</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
